<template>
  <v-form @submit.prevent="submitEditedDevice" ref="form">
    <v-card rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h5 font-weight-black ps-2">
          {{ $t('i18n.editDeviceModalTitle') }}
        </div>
        <v-btn icon="mdi-close" variant="text" @click="closeModal" size="x-large"></v-btn>
      </v-card-title>
      <v-card-text class="overflow-y-auto">
        <div class="font-weight-black">{{ $t('i18n.name') }}</div>
        <v-text-field density="compact" v-model="updatedDevice.name" variant="outlined" :rules="deviceNameRules"
          clearable></v-text-field>
        <div v-if="updatedDevice.type_id === 1">
          <div class="font-weight-black">{{ $t('i18n.uuidLabel') }}</div>
          <v-text-field density="compact" v-model="updatedDevice.uuid" variant="outlined" :rules="deviceUuidRules"
            hide-details="auto" clearable></v-text-field>
          <div class="mt-2 mb-6">{{ $t('i18n.uuidHint') }}</div>
        </div>
        <div class="font-weight-black">{{ $t('i18n.description') }}</div>
        <v-text-field density="compact" v-model="updatedDevice.description" variant="outlined"
          :rules="deviceDescriptionRules" clearable></v-text-field>
        <div class="font-weight-black">{{ $t('i18n.location') }}</div>
        <v-text-field density="compact" v-model="updatedDevice.location" variant="outlined" :rules="deviceLocationRules"
          clearable></v-text-field>
        <div class="font-weight-black">{{ $t('i18n.deviceTypeLabel') }}</div>
        <v-select :value="deviceTypeName(device.type_id)" disabled variant="outlined" density="compact"
          :hide-details="true" required :no-data-text="$t('i18n.noDeviceTypes')"></v-select>
        <div class="mt-2 mb-6 text-start">{{ $t('i18n.deviceTypeHint') }}</div>
        <div v-if="updatedDevice.type_id === 2"> <!-- QR CODE -->
          <div class="font-weight-black">{{ $t('i18n.completionRedirectUrl') }}</div>
          <v-text-field density="compact" v-model="updatedDevice.completion_redirect_url" hide-details="auto"
            variant="outlined" :rules="urlRules" clearable></v-text-field>
          <div class="mt-2 mb-6 text-start">{{ $t('i18n.completionRedirectUrlHint') }}</div>
        </div>
        <div v-if="updatedDevice.type_id === 1"> <!-- TABLET -->
          <div class="font-weight-black">{{ $t('i18n.ipAddress') }}</div>
          <v-text-field density="compact" v-model="updatedDevice.ip_address" variant="outlined" :rules="deviceIpRules"
            clearable></v-text-field>
        </div>
        <div class="font-weight-black">{{ $t('i18n.associateFormLabel') }}</div>
        <v-select v-model="updatedDevice.form_id" :items="simplifiedFormList" item-value="id" item-title="name"
          variant="outlined" density="compact" chips closable-chips :no-data-text="$t('i18n.noForms')"></v-select>
      </v-card-text>
      <v-card-actions>
        <v-btn class="gorate-btn-bg-outlined" variant="outlined" rounded="xl" :text="$t('i18n.closeBtnText')" @click="closeModal"></v-btn>
        <v-btn class="gorate-btn-bg" rounded="xl" :text="$t('i18n.saveBtnText')" type="submit"></v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Device, DeviceType, FormSchema } from '@/utils/DataTypes'
import { api, findDeviceTypeById } from '@/utils/HelperFunctions'
import { VForm } from 'vuetify/lib/components/index.mjs'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'EditDeviceModal',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  async mounted () {
    this.deviceTypeList = this.appStore.getAppState.deviceTypeList
    await this.getAllForms()
  },
  props: {
    currentDeviceList: {
      type: Array as PropType<Device[]>,
      required: true
    },
    device: {
      type: Object as PropType<Device>,
      required: true
    }
  },
  data () {
    return {
      formMap: new Map<string, FormSchema>(),
      simplifiedFormList: [] as Array<{ id: number | undefined; name: string }>,
      updatedDevice: { ...this.device },
      deviceTypeList: [] as Array<DeviceType>,
      ongoingRequest: false
    }
  },
  methods: {
    async getAllForms () {
      try {
        const url = '/forms'
        const getAllFormsResponse = await api(url, 'get')

        if (!getAllFormsResponse) return

        const formsObject = getAllFormsResponse.data as Record<string, any>
        this.formMap = new Map<string, FormSchema>(
          Object.entries(formsObject).map(([key, value]) => {
            const v = value as any
            return [
              key,
              {
                form: {
                  id: v.id,
                  name: v.name,
                  description: v.description,
                  layout_mode: v.layout_mode
                },
                form_items: v.form_items
              }
            ]
          })
        )
        this.simplifiedFormList = Array.from(this.formMap.values()).map((formSchema) => {
          return {
            id: formSchema.form.id,
            name: formSchema.form.name
          }
        })
      } catch (error) {
        this.appStore.addAlert('info', 'Couldn\'t get forms list.')
      }
    },
    async submitEditedDevice () {
      const newDeviceIsValid = await this.validateEditedDevice()
      if (!newDeviceIsValid) return
      try {
        this.ongoingRequest = true
        const deviceID = this.updatedDevice.id ?? -1
        // Como não se pode alterar o id e uuid, o backend nem sequer aceita que estes campos estejam presentes
        delete this.updatedDevice.id
        // delete this.updatedDevice.uuid

        const url = `/devices/${deviceID}`
        const editDeviceResponse = await api(url, 'put', this.updatedDevice)

        if (!editDeviceResponse) return

        if (editDeviceResponse.data.statusCode === 200) this.appStore.addAlert('success', editDeviceResponse.data.responseMessage)
        else this.appStore.addAlert('success', editDeviceResponse.data.responseMessage)
      } catch (error) {
        this.appStore.addAlert('error', 'Error editing device. Please try again later. If the problem persists contact support.')
      } finally {
        this.closeModal()
      }
    },
    async validateEditedDevice () {
      const form = this.$refs.form as VForm
      try {
        const { valid, errors } = await form.validate()

        if (errors && errors.length > 0) {
          console.error('Validation errors:', errors.map(err => ({
            id: err.id,
            messages: err.errorMessages
          })))
        }

        return valid
      } catch (error) {
        console.error('Validation Error: ', error)
        this.appStore.addAlert('error', 'Validation error. Please check input fields again.')
      }
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    deviceTypeName (deviceTypeID: number | null) {
      if (deviceTypeID === null) {
        return 'N/A'
      }
      return findDeviceTypeById(this.deviceTypeList, deviceTypeID)
    },
    closeModal () {
      this.$emit('close-modal')
    }
  },
  computed: {
    deviceNameRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          this.$t('i18n.deviceNameRequired')
        }, (value: string) => {
          if (value && value.length <= 30) return true
          return this.$t('i18n.deviceNameLength30')
        }, (value: string) => {
          if (value && value.length >= 3) return true
          return this.$t('i18n.fieldMinLength3')
        }, (value: string) => {
          const deviceNameAlreadyExists = this.currentDeviceList.some(device => device.name.trim() === value.trim() && this.device.name !== value)
          if (value && deviceNameAlreadyExists) {
            return this.$t('i18n.deviceNameExists')
          }
          return true
        }
      ]
    },
    deviceUuidRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.deviceUuidRequired')
        }, (value: string) => {
          // const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          // const uuidIsValid = uuidRegex.test(value)
          // if (uuidIsValid) return true
          if (value && value.length >= 8) return true
          return this.$t('i18n.deviceUuidInvalid')
        }, (value: string) => {
          const uuidAlreadyExists = this.currentDeviceList.some((device) => {
            if (device.uuid === null || device.uuid === undefined) {
              return false
            } else {
              return device.uuid.trim() === value.trim() && this.device.uuid !== value
            }
          })
          if (value && uuidAlreadyExists) {
            return this.$t('i18n.deviceUuidExists')
          }
          return true
        }
      ]
    },
    deviceDescriptionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) {
            if (value.length <= 30) return true
            return this.$t('i18n.fieldMaxLength30')
          }
          return true
        }
      ]
    },
    deviceLocationRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) {
            if (value.length < 30) return true
            return this.$t('i18n.fieldMaxLength30')
          }
          return true
        }
      ]
    },
    urlRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (!value) return true
          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const parsedUrl = new URL(value)
            return true
          } catch (e) {
            return this.$t('i18n.URLisInvalid')
          }
        }
      ]
    },
    deviceIpRules (): ((value: string) => boolean | string)[] {
      return [
        /* (value: string) => {
          if (value) return true
          return this.$t('i18n.deviceIpRequired')
        },  */
        (value: string) => {
          if (!value) return true
          const ipAlreadyExists = this.currentDeviceList.some((device) => {
            if (device.ip_address === null || device.ip_address === undefined) {
              return false
            } else {
              return device.ip_address.trim() === value.trim() && this.device.ip_address !== value
            }
          })
          if (value && ipAlreadyExists) {
            return this.$t('i18n.deviceIpExists')
          }
          return true
        }
      ]
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../../styles/colors.scss";

.button-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
</style>
